import { clampCyclesPerMinute } from "./blsMotion";

/**
 * The wire format for bilateral stimulation running inside a call.
 *
 * LiveKit multiplexes one data channel, so BLS traffic carries its own topic
 * and never has to be told apart from the whiteboard's or the meditation's.
 *
 * What travels here is the *settings and where the set is up to* — never the
 * picture or the sound. Both are produced on the patient's own device: a
 * screen-shared light bar is encoded at whatever frame rate the connection
 * allows, and a dot that jumps is worse than no dot for smooth pursuit; a
 * published audio track is mono, which would put the "left" tone in both ears.
 */
export const BLS_TOPIC = "alchely-bls";

export const BLS_MODES = ["visual", "audio", "both"] as const;
export type BlsMode = (typeof BLS_MODES)[number];

export const BLS_PATHS = ["horizontal", "diagonalUp", "diagonalDown", "infinity"] as const;
export type BlsPath = (typeof BLS_PATHS)[number];

export const BLS_DOT_SIZES = ["small", "medium", "large"] as const;
export type BlsDotSize = (typeof BLS_DOT_SIZES)[number];

export const BLS_DOT_COLORS = ["white", "green", "blue", "yellow"] as const;
export type BlsDotColor = (typeof BLS_DOT_COLORS)[number];

export type BlsSettings = {
    mode: BlsMode;
    path: BlsPath;
    /** Full left→right→left cycles per minute. Clamped to a safe range. */
    cyclesPerMinute: number;
    dotSize: BlsDotSize;
    dotColor: BlsDotColor;
    /**
     * Pulses in the patient's game controllers as well, on the same edges.
     * Added to whichever mode is chosen rather than being a mode of its own,
     * because it is usually combined with tones or the dot.
     */
    tactile: boolean;
};

/** What both sides show while a set is up. */
export type BlsState = {
    settings: BlsSettings;
    running: boolean;
    /**
     * Full cycles this set has completed as of `atEpochMs`, as a float.
     *
     * One number serves both screens: its fractional part is where the dot
     * is, its whole part is the therapist's pass counter. A speed change
     * carries the cycles reached so far, so the dot continues from where it
     * is instead of jumping.
     */
    cycles: number;
    /** When the sender measured `cycles`. Informational: receivers use their own clock. */
    atEpochMs: number;
};

export type BlsMessage =
    /** Start, pause, resume or a settings change: the whole state, so a dropped packet self-heals. */
    | { kind: "state"; state: BlsState }
    /** The set is over or the panel was closed. Back to the faces. */
    | { kind: "stop" }
    /** The patient asked to stop. Anyone may send it. */
    | { kind: "halt" }
    /** Someone joined late and needs to know whether a set is up. */
    | { kind: "sync-request" }
    /** The patient's side: how many vibrating controllers it can drive. */
    | { kind: "tactile-status"; controllers: number };

/**
 * Whether a participant may drive the stimulation for the room.
 *
 * The same rule as the meditation player and for the same reason: it reads
 * the identity the meeting service minted (`therapist-*` for an account
 * holder, `guest-*` for someone on a share link), and both sides apply it —
 * the sender to decide whether to offer the controls, the receiver to decide
 * whether to obey a packet.
 *
 * Stopping is deliberately *not* behind this rule: see `halt`.
 */
export function mayControlBls(identity: string): boolean {
    return identity.startsWith("therapist-");
}

const decoder = new TextDecoder();

function isOneOf<T extends string>(options: readonly T[], value: unknown): value is T {
    return typeof value === "string" && (options as readonly string[]).includes(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function parseSettings(value: unknown): BlsSettings | null {
    if (!isRecord(value)) return null;
    const { mode, path, cyclesPerMinute, dotSize, dotColor } = value;
    // Absent in packets from before tactile existed: read as off.
    const tactile = value.tactile ?? false;
    if (
        !isOneOf(BLS_MODES, mode) ||
        !isOneOf(BLS_PATHS, path) ||
        !isOneOf(BLS_DOT_SIZES, dotSize) ||
        !isOneOf(BLS_DOT_COLORS, dotColor) ||
        typeof cyclesPerMinute !== "number" ||
        typeof tactile !== "boolean"
    ) {
        return null;
    }
    return { mode, path, dotSize, dotColor, tactile, cyclesPerMinute: clampCyclesPerMinute(cyclesPerMinute) };
}

function parseState(value: unknown): BlsState | null {
    if (!isRecord(value)) return null;
    const settings = parseSettings(value.settings);
    const { running, cycles, atEpochMs } = value;
    if (
        !settings ||
        typeof running !== "boolean" ||
        typeof cycles !== "number" ||
        !Number.isFinite(cycles) ||
        cycles < 0 ||
        typeof atEpochMs !== "number"
    ) {
        return null;
    }
    return { settings, running, cycles, atEpochMs };
}

/**
 * Read a packet from the room, or `null` if it is not one we understand.
 *
 * A packet is whatever the other browser chose to send, so it is validated
 * field by field rather than cast: an unknown path would draw nothing, and a
 * speed outside the safe range is clamped here rather than obeyed — the
 * ceiling is a safety property, not a default.
 */
export function parseBlsMessage(payload: Uint8Array): BlsMessage | null {
    let raw: unknown;
    try {
        raw = JSON.parse(decoder.decode(payload));
    } catch {
        return null;
    }
    if (!isRecord(raw)) return null;
    switch (raw.kind) {
        case "state": {
            const state = parseState(raw.state);
            return state ? { kind: "state", state } : null;
        }
        case "stop":
        case "halt":
        case "sync-request":
            return { kind: raw.kind };
        case "tactile-status": {
            const { controllers } = raw;
            if (typeof controllers !== "number" || !Number.isInteger(controllers) || controllers < 0) {
                return null;
            }
            return { kind: "tactile-status", controllers };
        }
        default:
            return null;
    }
}
